import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin } from "lucide-react";
import { formatPropertyPriceParts, textLang, type Property } from "@triple-h/shared";
import { availability, formatMyanmarDate, propertyDisplayTitle } from "../lib/site";
import { propertyRef } from "../lib/seo";

export function PropertyCard({ property, priority = false }: { property: Property; priority?: boolean }) {
  const [imageFailed, setImageFailed] = useState(false);
  const status = availability(property);
  const price = formatPropertyPriceParts(property);
  const title = propertyDisplayTitle(property);
  const titleLang = textLang(title);
  const locationLang = textLang(property.locationText);
  const categoryLang = textLang(property.categoryName);
  const published = formatMyanmarDate(property.publishedAt);

  return (
    <article className="property-card">
      <Link className="card-media" to={`/property/${property.slug}`} aria-label={`View ${title}`}>
        {property.coverUrl && !imageFailed ? (
          <img
            src={property.coverUrl}
            alt={property.media?.[0]?.altText || title}
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : "auto"}
            decoding="async"
            width={property.coverWidth || 800}
            height={property.coverHeight || 600}
            onError={() => setImageFailed(true)}
          />
        ) : (
          <span className="card-placeholder">
            <img src="/brand/logo-mark.png" alt="" width="48" height="47" loading="lazy" />
            <span>Photograph coming soon</span>
          </span>
        )}
        <span className={`status-badge status-${status.tone}`}>
          <i aria-hidden="true" />
          {status.label}
        </span>
        <span className="card-ref">Ref {propertyRef(property.slug)}</span>
      </Link>

      <div className="card-body">
        <p className="card-meta">
          <span lang={categoryLang}>{property.categoryName || "Property"}</span>
          {published && <><span className="meta-dot" aria-hidden="true" /> <span lang="my">{published}</span></>}
        </p>
        <h3 lang={titleLang}>
          <Link to={`/property/${property.slug}`}>{title}</Link>
        </h3>
        {property.locationText && (
          <p className="card-location" lang={locationLang}><MapPin aria-hidden="true" />{property.locationText}</p>
        )}
        <div className="card-foot">
          <p className="card-price" lang="my">
            {price.primary}
            {price.secondary && <small lang="en">{price.secondary}</small>}
          </p>
          <Link className="card-arrow" to={`/property/${property.slug}`} aria-label={`View ${title}`}>
            <ArrowUpRight aria-hidden="true" />
          </Link>
        </div>
      </div>
    </article>
  );
}
